/**
 * AccessClaim — landing page for the magic-link in the user's approval email.
 *
 * URL: /access/claim?token=<JWT>&next=<path-to-redirect-to>
 *
 *  1. Reads the token from the query string.
 *  2. Verifies it with the backend (catches a revoked / forged token early).
 *  3. On success: stores it in localStorage and redirects to ?next= (default
 *    /tools/sizing-capacity/pump-sizing).
 *  4. On failure: friendly error + link back to the request form.
 */
import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_TOKEN = "hv_access_token";
const STORAGE_EMAIL = "hv_access_email";

export default function AccessClaim() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [phase, setPhase] = useState("claiming");   // claiming | error
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const token = params.get("token");
    const next  = params.get("next") || "/tools/sizing-capacity/pump-sizing";
    if (!token) {
      setPhase("error");
      setErrorMsg("This activation link is missing its token. Please use the latest link from your approval email.");
      return;
    }
    (async () => {
      try {
        const r = await fetch(`${API}/api/access/verify`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });
        const data = await r.json();
        if (!data.ok) {
          setPhase("error");
          setErrorMsg(
            data.status === "revoked"
              ? "Your access has been revoked. Please contact the HidroVerse team."
              : "This link has expired or been replaced by a newer one. Request a fresh link below."
          );
          return;
        }
        localStorage.setItem(STORAGE_TOKEN, token);
        if (data.email) localStorage.setItem(STORAGE_EMAIL, data.email);
        navigate(next, { replace: true });
      } catch (e) {
        setPhase("error");
        setErrorMsg("Could not reach the HidroVerse API. Please try again in a moment.");
      }
    })();
  }, [params, navigate]);

  return (
    <main
      className="min-h-screen flex items-center justify-center px-6 py-24"
      style={{ background: "#0C2A40", color: "#F5F7FA" }}
      data-testid="access-claim-page"
    >
      <Helmet>
        <title>Activating access — HidroVerse</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="max-w-md w-full">
        <div
          className="font-mono uppercase mb-3"
          style={{ fontSize: "0.62rem", letterSpacing: "0.22em", color: "#1FB6C1", fontWeight: 700 }}
        >
          HidroVerse · engineering tools
        </div>
        {phase === "claiming" && (
          <>
            <h1 style={{ fontSize: "1.6rem", fontWeight: 700, marginBottom: 14 }}>Activating your access…</h1>
            <p className="font-light" style={{ fontSize: "0.95rem", lineHeight: 1.65, color: "rgba(245,247,250,0.75)" }}>
              One moment while we verify your token and unlock the engineering tools on this device.
            </p>
          </>
        )}
        {phase === "error" && (
          <>
            <h1 style={{ fontSize: "1.6rem", fontWeight: 700, marginBottom: 14 }}>This link can't be used</h1>
            <p className="font-light" style={{ fontSize: "0.95rem", lineHeight: 1.65, color: "rgba(245,247,250,0.85)" }}>
              {errorMsg}
            </p>
            <div className="mt-6 flex gap-3 flex-wrap">
              <Link
                to="/tools/sizing-capacity/pump-sizing"
                data-testid="access-claim-retry"
                className="inline-block px-5 py-2.5 font-mono uppercase"
                style={{ background: "#1FB6C1", color: "#0C2A40", fontSize: "0.7rem", letterSpacing: "0.18em", fontWeight: 700 }}
              >
                Request a fresh link
              </Link>
              <Link
                to="/contact"
                className="inline-block px-5 py-2.5 font-mono uppercase"
                style={{ background: "transparent", color: "#F5F7FA", border: "1px solid rgba(245,247,250,0.30)", fontSize: "0.7rem", letterSpacing: "0.18em", fontWeight: 700 }}
              >
                Contact HidroVerse
              </Link>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
